import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Gift } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PreorderSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const presaveId = searchParams.get('presave_id');
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const checkStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/presaves/preorder/status/${sessionId}`);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        logger.error('Pre-order status check error:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('error');
        }
      }
    };

    checkStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen flex items-center justify-center pt-20 px-4" data-testid="preorder-success-page">
      <div className="glass-card p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-purple-400 mx-auto mb-4 animate-spin" />
            <h1 className="text-2xl font-bold mb-2">Processing Your Pre-order</h1>
            <p className="text-gray-400">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Pre-order Confirmed!</h1>
            <p className="text-gray-400 mb-6">
              Your bonus is ready and a confirmation email is on its way. The full track lands in your library the moment it releases.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {presaveId && (
                <Link to={`/bonus/${presaveId}`} className="btn-primary" data-testid="go-to-bonus">
                  <Gift className="w-4 h-4 mr-2" /> View Your Bonus
                </Link>
              )}
              <Link to="/presaves" className="btn-secondary" data-testid="back-to-presaves">
                Back to Releases
              </Link>
            </div>
          </>
        )}

        {status === 'pending' && (
          <>
            <Loader2 className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Payment Processing</h1>
            <p className="text-gray-400 mb-6">
              Your payment is still being processed. You&apos;ll receive a confirmation email shortly.
            </p>
            <Link to="/presaves" className="btn-primary" data-testid="back-to-presaves">
              Back to Releases
            </Link>
          </>
        )}

        {(status === 'error' || status === 'expired') && (
          <>
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">
              {status === 'expired' ? 'Pre-order Expired' : 'Pre-order Failed'}
            </h1>
            <p className="text-gray-400 mb-6">
              {status === 'expired'
                ? 'Your pre-order session has expired. Please try again.'
                : 'There was an issue processing your pre-order. Please try again.'}
            </p>
            <Link to="/presaves" className="btn-primary" data-testid="back-to-presaves">
              Back to Releases
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
